import _ from "lodash";
import {
  Collection,
  FileItem,
  Matter,
  MatterDocument,
  MatterTreeNode,
} from "common/types/matter";

const DOCUMENT_REVISION = 2;
const DOCUMENT_POOL = 3;
const COMBINED_DOCUMENT = 4;
const PURSUANT_TO = 2;

const typeNames: Record<number, string> = {1: "MSA", 2: "MSOW", 3: "SOW"};
// Order contract-type groups SOW before MSOW under a parent.
const typeOrder = [3, 2, 1];

function buildDocNode(
  doc: MatterDocument,
  children: MatterTreeNode[] = [],
): MatterTreeNode {
  return {
    id: doc.id,
    type: "document",
    name: doc.filename,
    data: doc,
    children,
  };
}

function buildGroupNode(
  typeId: number,
  poolId: number,
  parentId: MatterTreeNode["id"],
  children: MatterTreeNode[],
): MatterTreeNode {
  const name = typeNames[typeId] ?? "";
  return {
    id: poolId,
    type: "collection",
    name,
    data: {
      id: poolId,
      name,
      collection_type_id: DOCUMENT_POOL,
      collection_type: "DocumentPool",
      dc_document_type: typeId,
      parent_id: parentId,
      child_ids: [],
      relations: [],
      level: 0,
    },
    children,
  };
}

// A node for a CombinedDocument: several files uploaded as a single logical
// document. `name` is the collection name the user gave the group; the
// individual files hang beneath it.
function buildCombinedNode(
  collectionId: number,
  name: string,
  typeId: number,
  parentId: MatterTreeNode["id"],
  children: MatterTreeNode[],
): MatterTreeNode {
  return {
    id: collectionId,
    type: "collection",
    name,
    data: {
      id: collectionId,
      name,
      collection_type_id: COMBINED_DOCUMENT,
      collection_type: "CombinedDocument",
      dc_document_type: typeId,
      parent_id: parentId,
      child_ids: [],
      relations: [],
      level: 0,
    },
    children,
  };
}

export const buildTreeStructure = (
  documents: MatterDocument[] = [],
  collections: Matter["collections"] = [],
  hideEmpty = false,
): MatterTreeNode[] => {
  const collectionById = new Map<number, Collection>(
    collections.map(collection => [collection.id, collection]),
  );

  // Walk up the parent chain until a collection of the given type is found.
  // Guards against cycles/orphans by tracking visited ids and bailing to undefined.
  const nearestAncestor = (
    collectionId: number | null | undefined,
    collectionTypeId: number,
  ): Collection | undefined => {
    const visited = new Set<number>();
    let current =
      collectionId === null || collectionId === undefined
        ? undefined
        : collectionById.get(collectionId);
    while (current && !visited.has(current.id)) {
      if (current.collection_type_id === collectionTypeId) {
        return current;
      }
      visited.add(current.id);
      current =
        current.parent_id === null
          ? undefined
          : collectionById.get(current.parent_id);
    }
    return undefined;
  };

  // Map a DocumentRevision id -> the document that represents it (MSA/MSOW doc),
  // so a pool's `pursuant_to` target revision resolves back to a parent document.
  const documentByRevision = new Map<number, MatterDocument>();
  documents.forEach(doc => {
    const revision = nearestAncestor(doc.collection_id, DOCUMENT_REVISION);
    if (revision) {
      documentByRevision.set(revision.id, doc);
    }
  });

  // Resolve each document's parent document via its enclosing pool's pursuant_to
  // relation. A document with no enclosing pool is a root (an MSA).
  const childrenByParent = new Map<number, MatterDocument[]>();
  // The pool a document belongs to, used to label/group its contract-type folder.
  const poolIdByDoc = new Map<number, number>();
  const roots: MatterDocument[] = [];

  documents.forEach(doc => {
    const pool = nearestAncestor(doc.collection_id, DOCUMENT_POOL);
    if (!pool) {
      roots.push(doc);
      return;
    }
    poolIdByDoc.set(doc.id, pool.id);
    const pursuant = pool.relations.find(
      relation => relation.relation_type_id === PURSUANT_TO,
    );
    const targetRevisionId = pursuant?.link_a_collection_id;
    const parent =
      targetRevisionId === undefined
        ? undefined
        : documentByRevision.get(targetRevisionId);
    if (!parent) {
      // Unresolved target — treat as a root rather than dropping the document.
      roots.push(doc);
      return;
    }
    const siblings = childrenByParent.get(parent.id) ?? [];
    siblings.push(doc);
    childrenByParent.set(parent.id, siblings);
  });

  // Recursively build a document node: group its child documents by contract type.
  // Each contract-type group always gets its own folder node, even for a single
  // document.
  const buildNode = (doc: MatterDocument): MatterTreeNode => {
    const childDocs = childrenByParent.get(doc.id) ?? [];
    const byType = _.groupBy(childDocs, "document_type_id") as Record<
      string,
      MatterDocument[]
    >;

    const childNodes: MatterTreeNode[] = [];
    typeOrder.forEach(typeId => {
      const docsOfType = byType[String(typeId)];
      if (!docsOfType || docsOfType.length === 0) {
        return;
      }

      // Documents that share a CombinedDocument were uploaded as a single
      // logical document (the user-named collection). Group them under one node
      // carrying that name, so the name is shown rather than the bare type. A
      // CombinedDocument holding just one document stays a plain document node.
      const groupsByCombined = new Map<number, MatterDocument[]>();
      const groupOrder: number[] = [];
      docsOfType.forEach(childDoc => {
        const combined = nearestAncestor(
          childDoc.collection_id,
          COMBINED_DOCUMENT,
        );
        const key = combined?.id ?? childDoc.id;
        const group = groupsByCombined.get(key);
        if (group) {
          group.push(childDoc);
        } else {
          groupsByCombined.set(key, [childDoc]);
          groupOrder.push(key);
        }
      });

      const nodes = groupOrder.map(key => {
        const groupDocs = groupsByCombined.get(key) as MatterDocument[];
        if (groupDocs.length === 1) {
          return buildNode(groupDocs[0]);
        }
        const combined = collectionById.get(key);
        const combinedName =
          combined && (combined.name ?? "").length > 0
            ? (combined.name as string)
            : groupDocs[0].filename;
        return buildCombinedNode(
          key,
          combinedName,
          typeId,
          doc.id,
          groupDocs.map(buildNode),
        );
      });

      const poolId = poolIdByDoc.get(docsOfType[0].id) ?? typeId;
      childNodes.push(buildGroupNode(typeId, poolId, doc.id, nodes));
    });

    return buildDocNode(doc, childNodes);
  };

  // Drop folder nodes that contain no documents anywhere in their subtree.
  const hasDocument = (node: MatterTreeNode): boolean =>
    node.type === "document" || node.children.some(hasDocument);
  const prune = (node: MatterTreeNode): MatterTreeNode => ({
    ...node,
    children: node.children
      .filter(child => child.type === "document" || hasDocument(child))
      .map(prune),
  });

  // Root documents sharing a CombinedDocument render as one combined group of
  // files, with their dependent folders hoisted out as following siblings.
  const groupRoots = (docs: MatterDocument[]): MatterTreeNode[] => {
    const byCombined = new Map<number, MatterDocument[]>();
    const order: number[] = [];
    docs.forEach(rootDoc => {
      const combined = nearestAncestor(
        rootDoc.collection_id,
        COMBINED_DOCUMENT,
      );
      const key = combined?.id ?? rootDoc.id;
      const group = byCombined.get(key);
      if (group) {
        group.push(rootDoc);
      } else {
        byCombined.set(key, [rootDoc]);
        order.push(key);
      }
    });
    return order.flatMap(key => {
      const groupDocs = byCombined.get(key) as MatterDocument[];
      if (groupDocs.length === 1) {
        return [buildNode(groupDocs[0])];
      }
      const combined = collectionById.get(key);
      const combinedName =
        combined && (combined.name ?? "").length > 0
          ? (combined.name as string)
          : groupDocs[0].filename;
      const memberNodes = groupDocs.map(buildNode);
      const dependents = memberNodes.flatMap(member => member.children);
      const files = memberNodes.map(member => ({...member, children: []}));
      return [
        buildCombinedNode(
          key,
          combinedName,
          groupDocs[0].document_type_id,
          0,
          files,
        ),
        ...dependents,
      ];
    });
  };

  const tree = groupRoots(roots);
  return hideEmpty ? tree.map(prune) : tree;
};

const MSOW_TYPE = 2;
const SOW_TYPE = 3;

// Which nodes should start collapsed while a pending upload is being previewed,
// so the tree stays focused on where the upload lands:
// - Uploading a SOW under the MSA: collapse every MSOW document that hangs off
//   the MSA (which hides its SOW folder with it).
// - Uploading a SOW under an MSOW: collapse every other MSOW document, the MSA's
//   own SOW folder, and any combined documents inside the target MSOW.
// An out-of-focus SOW folder that hangs off an MSOW is collapsed by collapsing
// that MSOW document itself; a top-level (MSA) SOW folder collapses on its own.
// Nodes whose subtree contains the pending upload are never collapsed, so the
// preview is always visible.
export const computeDefaultCollapsedIds = (
  tree: MatterTreeNode[],
  targetNodeId: number | undefined,
): Set<number> => {
  const collapsed = new Set<number>();
  if (targetNodeId === undefined) {
    return collapsed;
  }

  // The target document's contract type decides which case applies.
  let targetTypeId: number | undefined;
  const findTarget = (nodes: MatterTreeNode[]): void => {
    nodes.forEach(node => {
      if (node.id === targetNodeId && node.type === "document") {
        targetTypeId = (node.data as MatterDocument).document_type_id;
      }
      findTarget(node.children);
    });
  };
  findTarget(tree);
  if (targetTypeId === undefined) {
    return collapsed;
  }
  const targetIsMsow = targetTypeId === MSOW_TYPE;

  const containsPending = (node: MatterTreeNode): boolean =>
    Boolean(node.pending) || node.children.some(containsPending);

  const walk = (
    node: MatterTreeNode,
    parent: MatterTreeNode | undefined,
    withinTarget: boolean,
  ): void => {
    if (node.type === "collection" && !containsPending(node)) {
      const collection = node.data as Collection;
      const isCombined = collection.collection_type === "CombinedDocument";
      const isSowGroup =
        !isCombined && collection.dc_document_type === SOW_TYPE;
      // SOW groups that aren't the target document's own are out of focus.
      if (isSowGroup && parent && parent.id !== targetNodeId) {
        const parentIsMsow =
          parent.type === "document" &&
          (parent.data as MatterDocument).document_type_id === MSOW_TYPE;
        // Collapse the parent MSOW document itself (hiding this folder with it);
        // a top-level (MSA) SOW folder just collapses on its own. Leave the
        // parent expanded if it holds the pending upload.
        if (parentIsMsow) {
          if (!containsPending(parent)) {
            collapsed.add(parent.id);
          }
        } else {
          collapsed.add(node.id);
        }
      }
      // Combined documents inside the target MSOW are collapsed to declutter.
      if (targetIsMsow && isCombined && withinTarget) {
        collapsed.add(node.id);
      }
    }
    const childWithinTarget =
      withinTarget || (node.type === "document" && node.id === targetNodeId);
    node.children.forEach(child => walk(child, node, childWithinTarget));
  };
  tree.forEach(node => walk(node, undefined, false));

  return collapsed;
};

// While a user is adding files to a particular MSOW, keep that MSOW at the top
// of its MSOW folder so it stays visible without scrolling. Within each MSOW
// group, the child whose subtree contains `targetNodeId` (the MSOW itself, or a
// combined document holding it) is moved to the front; the rest keep their
// order. Returns a new tree; the input is not mutated. A no-op when the target
// isn't inside any MSOW group.
export const promoteTargetMsow = (
  tree: MatterTreeNode[],
  targetNodeId: number | undefined,
): MatterTreeNode[] => {
  if (targetNodeId === undefined) {
    return tree;
  }

  const containsTarget = (node: MatterTreeNode): boolean =>
    node.id === targetNodeId || node.children.some(containsTarget);

  const reorder = (node: MatterTreeNode): MatterTreeNode => {
    let children = node.children.map(reorder);
    if (
      node.type === "collection" &&
      (node.data as Collection).collection_type !== "CombinedDocument" &&
      (node.data as Collection).dc_document_type === MSOW_TYPE
    ) {
      const idx = children.findIndex(containsTarget);
      if (idx > 0) {
        const [target] = children.splice(idx, 1);
        children = [target, ...children];
      }
    }
    return {...node, children};
  };

  return tree.map(reorder);
};

// A placeholder MatterDocument for an unsubmitted upload, enough to render a
// pending preview node (real fields are populated once the upload completes).
function buildPendingDoc(
  id: number,
  filename: string,
  typeId: number,
): MatterDocument {
  return {
    id,
    filename,
    document_type_id: typeId,
    collection_id: 0,
    user_id: 0,
    username: "",
    creation_date: new Date().toISOString(),
    load_state: 0,
    parse_state: 0,
    concat_load_state: [],
    concat_parse_state: [],
  };
}

// Inject synthetic "pending" preview documents under the document node
// `targetNodeId`, so a user previewing a targeted upload sees where their files
// will land before submitting. Pending files are merged into the target's
// existing contract-type folder when one is present (matching how a real upload
// lands), and only get a new folder when the target has none of that type yet.
// Pending nodes get unique negative ids to avoid colliding with real node
// ids/keys. Returns a new tree; the input is not mutated.
export const injectPendingUpload = (
  tree: MatterTreeNode[],
  targetNodeId: number,
  fileItems: Array<Pick<FileItem, "file" | "documentType">>,
): MatterTreeNode[] => {
  if (fileItems.length === 0) {
    return tree;
  }

  let nextId = -1;
  const byType = _.groupBy(
    fileItems,
    item => item.documentType ?? "none",
  ) as Record<string, Array<Pick<FileItem, "file" | "documentType">>>;

  // Pending document nodes per contract type, in typeOrder.
  const pendingDocsByType = new Map<number, MatterTreeNode[]>();
  typeOrder.forEach(typeId => {
    const items = byType[typeNames[typeId]];
    if (!items || items.length === 0) {
      return;
    }
    pendingDocsByType.set(
      typeId,
      items.map(item => ({
        ...buildDocNode(buildPendingDoc(nextId--, item.file.name, typeId)),
        pending: true,
      })),
    );
  });

  if (pendingDocsByType.size === 0) {
    return tree;
  }

  // Mirror the backend: files of the same contract type uploaded together are
  // grouped into a single CombinedDocument, so preview them as one pending
  // combined node. A lone file of a type stays a plain document node.
  pendingDocsByType.forEach((docNodes, typeId) => {
    if (docNodes.length <= 1) {
      return;
    }
    const firstDoc = docNodes[0].data as MatterDocument;
    pendingDocsByType.set(typeId, [
      {
        ...buildCombinedNode(nextId--, firstDoc.filename, typeId, 0, docNodes),
        pending: true,
      },
    ]);
  });

  // Mirror the backend: an MSOW uploaded alongside SOWs adopts those SOWs into
  // its own nested SOW pool (pursuant_to the MSOW), so the SOWs are analysed
  // under the MSOW rather than sitting pursuant_to the MSA. Nest the pending SOW
  // folder under the pending MSOW so the preview lands where the upload will.
  const pendingMsows = pendingDocsByType.get(MSOW_TYPE);
  const pendingSows = pendingDocsByType.get(SOW_TYPE);
  if (pendingMsows && pendingMsows.length > 0 && pendingSows) {
    const [firstMsow, ...restMsows] = pendingMsows;
    const nestedSowFolder: MatterTreeNode = {
      ...buildGroupNode(SOW_TYPE, nextId--, firstMsow.id, pendingSows),
      pending: true,
    };
    pendingDocsByType.set(MSOW_TYPE, [
      {...firstMsow, children: [...firstMsow.children, nestedSowFolder]},
      ...restMsows,
    ]);
    pendingDocsByType.delete(SOW_TYPE);
  }

  const attachToTarget = (node: MatterTreeNode): MatterTreeNode[] => {
    const targetTypeId = (node.data as MatterDocument).document_type_id;
    const mergedTypes = new Set<number>();
    // Merge pending docs into the target's existing folder of the same type.
    const children = node.children.map(child => {
      if (child.type !== "collection") {
        return child;
      }
      const typeId = (child.data as Collection).dc_document_type;
      // The target's own type is a new revision of the target document, handled
      // as a direct file child below rather than merged into a folder.
      if (typeId === targetTypeId) {
        return child;
      }
      const pendingDocs = pendingDocsByType.get(typeId);
      if (!pendingDocs) {
        return child;
      }
      mergedTypes.add(typeId);
      // Pending docs sit above the existing ones so the in-progress upload is
      // surfaced as high as possible in the folder.
      return {...child, children: [...pendingDocs, ...child.children]};
    });
    // For types with no existing folder, add a new (pending) folder.
    const newFolders: MatterTreeNode[] = [];
    pendingDocsByType.forEach((docs, typeId) => {
      if (typeId === targetTypeId || mergedTypes.has(typeId)) {
        return;
      }
      newFolders.push({
        ...buildGroupNode(typeId, nextId--, node.id, docs),
        pending: true,
      });
    });
    const sameTypeDocs = pendingDocsByType.get(targetTypeId) ?? [];
    const dependents = [...newFolders, ...children];

    if (sameTypeDocs.length === 0) {
      return [{...node, children: dependents}];
    }

    // A new file of the target's own type previews as a combined document of
    // the files, with dependent folders following as siblings.
    const existingFile = buildDocNode(node.data as MatterDocument);
    return [
      buildCombinedNode(
        nextId--,
        (node.data as MatterDocument).filename,
        targetTypeId,
        0,
        [existingFile, ...sameTypeDocs],
      ),
      ...dependents,
    ];
  };

  // Target is inside a real combined group: same-type pending files append into
  // the group; other types merge into sibling folders or new ones after it.
  const attachAroundCombined = (
    nodes: MatterTreeNode[],
    combinedIndex: number,
  ): MatterTreeNode[] => {
    const combinedNode = nodes[combinedIndex];
    const combinedTypeId = (combinedNode.data as Collection).dc_document_type;
    const sameTypeDocs = pendingDocsByType.get(combinedTypeId) ?? [];
    const mergedTypes = new Set<number>();
    const merged = nodes.map((sibling, index) => {
      if (index === combinedIndex) {
        return sameTypeDocs.length > 0
          ? {...sibling, children: [...sibling.children, ...sameTypeDocs]}
          : sibling;
      }
      if (
        sibling.type === "collection" &&
        (sibling.data as Collection).collection_type !== "CombinedDocument"
      ) {
        const folderTypeId = (sibling.data as Collection).dc_document_type;
        const pendingDocs =
          folderTypeId === combinedTypeId
            ? undefined
            : pendingDocsByType.get(folderTypeId);
        if (pendingDocs) {
          mergedTypes.add(folderTypeId);
          return {...sibling, children: [...pendingDocs, ...sibling.children]};
        }
      }
      return sibling;
    });
    const newFolders: MatterTreeNode[] = [];
    pendingDocsByType.forEach((docs, typeId) => {
      if (typeId === combinedTypeId || mergedTypes.has(typeId)) {
        return;
      }
      newFolders.push({
        ...buildGroupNode(typeId, nextId--, combinedNode.id, docs),
        pending: true,
      });
    });
    return [
      ...merged.slice(0, combinedIndex + 1),
      ...newFolders,
      ...merged.slice(combinedIndex + 1),
    ];
  };

  const insertList = (nodes: MatterTreeNode[]): MatterTreeNode[] => {
    const combinedIndex = nodes.findIndex(
      node =>
        node.type === "collection" &&
        (node.data as Collection).collection_type === "CombinedDocument" &&
        node.children.some(
          child => child.type === "document" && child.id === targetNodeId,
        ),
    );
    if (combinedIndex !== -1) {
      return attachAroundCombined(nodes, combinedIndex);
    }
    return nodes.flatMap(node => {
      if (node.id === targetNodeId && node.type === "document") {
        return attachToTarget(node);
      }
      return [{...node, children: insertList(node.children)}];
    });
  };
  return insertList(tree);
};
